import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  FootPrintStatus,
  ICalculationResponse,
  IFootPrintDetails,
} from '../shared/interfaces/IFootprint';
import { IProduct } from '../shared/interfaces/IProduct';
import { useGetCalculationById, useGetFootprintById } from './api/footprints';
import { useGetProductById } from './api/products';
import { useAuth } from './auth/useAuth';

export function useFootprintAndProduct(): {
  product?: IProduct;
  footprintDetails?: IFootPrintDetails;
  isFootprintLoading: boolean;
  calculationDetails?: ICalculationResponse;
  isCalculationLoading: boolean;
  isGetCalculationError: boolean;
  isProductLoading: boolean;
  showEditButton: boolean;
} {
  // Get footprintId and productId from URL
  const {
    footprintId,
    productId,
    calculationId: urlCalculationId,
  } = useParams() as {
    footprintId: string;
    calculationId: string;
    productId: string;
  };

  const location = useLocation();

  // Fetch product details based on the productId
  const { data: product, isLoading: isProductLoading } = useGetProductById(productId, {
    enabled: !!productId,
    retry: 0,
  });

  // Getting the footprint details
  const { data: footprintDetails, isLoading: isFootprintLoading } = useGetFootprintById(
    Number(footprintId),
    {
      enabled: !!footprintId,
      retry: 0,
    },
  );
  // During "Copy as new", fall back to the published calculation ID from footprint details

  const calculationId =
    location.pathname.endsWith('copy') && !urlCalculationId
      ? footprintDetails?.publishedCalculationId
      : urlCalculationId;

  // Getting the calculation details
  const {
    data: calculationDetails,
    isInitialLoading: isCalculationLoading,
    isError: isGetCalculationError,
  } = useGetCalculationById(Number(footprintId), Number(calculationId), {
    enabled: !!footprintId && !!calculationId,
    retry: 0,
  });

  const { isBasfNonAdmin } = useAuth();

  // Determine if the edit button should be shown
  // Show edit button if the calculation and footprint are in DRAFT status and the product is not readonly
  // Also hide edit button for BASF non-admin users
  const showEditButton = useMemo(() => {
    return (
      footprintDetails?.status === FootPrintStatus.DRAFT && !product?.isReadOnly && !isBasfNonAdmin
    );
  }, [footprintDetails, product, isBasfNonAdmin]);

  return {
    product,
    footprintDetails,
    isFootprintLoading,
    isCalculationLoading,
    isGetCalculationError,
    calculationDetails,
    showEditButton,
    isProductLoading,
  };
}
